/**
 * FK Constraint Resolver
 *
 * Resolves FK target (referenced table/column) from table constraint metadata.
 * Used by LazyForeignKeyCell to lazily resolve FK targets on click.
 */

import type { ConstraintMetadata, LooseRelationship } from '../../../../types/electron';
import { buildForeignKeyMap, lookupForeignKey, type ColumnForeignKeyInfo } from '../../table-explorer/hooks/useTableMetadata';

/**
 * Find the FK constraint info for a specific column from a table's constraints.
 * Returns the referenced schema/table/column if found, or null if the column
 * has no FK constraint (engine reported isForeignKey incorrectly).
 *
 * The target is read from Table Explorer's own `buildForeignKeyMap`, so the
 * grid-cell preview can never pick another constraint than the maps the cell
 * editor, the enum editor's "Search rows" and Table Explorer read
 * (`compareFkConstraints`: a single-column constraint before a composite one,
 * then by constraint name). A column that is only a composite-FK member
 * yields to `looseRelationships[0]` — the column's own loose declarations,
 * already scoped to the source triple — which is then returned with
 * `isLoose: true` (`realFkOutranksLoose`).
 *
 * The column is read by its exact name (`lookupForeignKey`): `"ref id"` never
 * previews `"ref-id"`'s target. `columnNames` — the source table's columns,
 * when known — lets a source column spelled in another case find its one
 * column, and keeps a distinct case variant from borrowing another's FK.
 */
export function resolveFkTarget(
  constraints: ConstraintMetadata[] | undefined,
  sourceColumn: string,
  looseRelationships: readonly LooseRelationship[] = [],
  columnNames?: readonly string[],
): ColumnForeignKeyInfo | null {
  if (!constraints || !sourceColumn) return null;
  const target = lookupForeignKey(buildForeignKeyMap(constraints, [], columnNames), sourceColumn);
  if (!target) return null;
  const { isComposite, ...info } = target;
  const loose = looseRelationships[0];
  if (!isComposite || !loose) return info;
  return {
    constraintName: `loose:${loose.id}`,
    referencedSchema: loose.toSchema,
    referencedTable: loose.toTable,
    referencedColumn: loose.toColumn,
    isLoose: true,
  };
}
